/**
 * Staff session: the token from `POST /auth/staff/login`, kept in `sessionStorage`. There is no refresh
 * token in the backend contract, so an expired session means signing in again. Role is stored for display
 * and navigation hints only; the backend decides what a token may do.
 */
import type { StaffLoginResult, StaffRole } from '@/api/types';
import { jwtExpiryMs } from './jwt';
import { createSessionStore } from './session-store';

export const STAFF_ROLES: readonly StaffRole[] = [
  'ROLE_WAITER',
  'ROLE_KITCHEN',
  'ROLE_CASHIER',
  'ROLE_BRANCH_MANAGER',
  'ROLE_BRAND_OWNER',
  'ROLE_SUPER_ADMIN',
];

export interface StaffSession {
  token: string;
  expiresAt: number | null;
  user: {
    id: string;
    displayName: string;
    email: string;
    role: StaffRole;
    tenantId: string;
    branchId: string | null;
  };
}

const isString = (value: unknown): value is string => typeof value === 'string' && value !== '';

function parseStaffSession(raw: unknown): StaffSession | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  const user = v.user;
  if (typeof user !== 'object' || user === null) return null;
  const u = user as Record<string, unknown>;
  if (
    !isString(v.token) ||
    (v.expiresAt !== null && typeof v.expiresAt !== 'number') ||
    !isString(u.id) ||
    typeof u.displayName !== 'string' ||
    typeof u.email !== 'string' ||
    !STAFF_ROLES.includes(u.role as StaffRole) ||
    !isString(u.tenantId) ||
    (u.branchId !== null && !isString(u.branchId))
  ) {
    return null;
  }
  return {
    token: v.token,
    expiresAt: v.expiresAt,
    user: {
      id: u.id,
      displayName: u.displayName,
      email: u.email,
      role: u.role as StaffRole,
      tenantId: u.tenantId,
      branchId: u.branchId,
    },
  };
}

export const staffSessionStore = createSessionStore<StaffSession>({
  key: 'rp.staff-session.v1',
  parse: parseStaffSession,
});

/** Prefers the token's own `exp`; falls back to `expires_in` seconds from now. */
export function staffSessionFromLogin(
  result: StaffLoginResult,
  now: number = Date.now(),
): StaffSession {
  return {
    token: result.access_token,
    expiresAt: jwtExpiryMs(result.access_token) ?? now + result.expires_in * 1000,
    user: {
      id: result.user.id,
      displayName: result.user.display_name,
      email: result.user.email,
      role: result.user.role,
      tenantId: result.user.tenant_id,
      branchId: result.user.branch_id,
    },
  };
}
